import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const ADMIN_STATUSES = [
  "Applied",
  "Tutor On Demo",
  "Tutor Confirmed",
  "Application Closed",
  "Tutor Rejected Tuition",
  "Guardian Rejected Tutor",
  "Lost",
];

const COMMISSION_STATUSES = [
  "Payment Pending",
  "Partially Paid",
  "Paid",
  "Waived",
  "Disputed",
  "Uncollectible",
];

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString()} BDT`;
}

function whatsappHref(number) {
  let digits = String(number || "").replace(/\D/g, "");

  if (digits.startsWith("0")) {
    digits = `880${digits.slice(1)}`;
  }

  return digits
    ? `https://web.whatsapp.com/send/?phone=${digits}&text&type=phone_number&app_absent=0`
    : "#";
}

function normalizeMessengerLink(value) {
  const clean = String(value || "").trim();

  if (!clean) return "";

  if (/^https?:\/\//i.test(clean)) {
    return clean;
  }

  return `https://${clean}`;
}

function statusTone(status) {
  if (
    status === "Application Closed" ||
    status === "Tutor Confirmed" ||
    status === "Paid" ||
    status === "Waived"
  ) {
    return "success";
  }

  if (
    status === "Lost" ||
    status === "Tutor Rejected Tuition" ||
    status === "Guardian Rejected Tutor" ||
    status === "Disputed" ||
    status === "Uncollectible"
  ) {
    return "danger";
  }

  if (
    status === "Tutor On Demo" ||
    status === "Payment Pending" ||
    status === "Partially Paid"
  ) {
    return "warning";
  }

  if (status === "Applied") {
    return "blue";
  }

  return "default";
}

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-slate-50 text-slate-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-black ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  variant = "default",
  disabled,
  title,
  className = "",
}) {
  const variants = {
    default:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
    dark: "border border-slate-950 bg-slate-950 text-white hover:bg-slate-800",
    success:
      "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    blue: "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-2.5 text-sm font-black shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
        variants[variant] || variants.default
      } ${className}`}
    >
      {children}
    </button>
  );
}

function MetricCard({ label, value, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-white text-slate-950",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm ${
        tones[tone] || tones.default
      }`}
    >
      <p className="text-xs font-black uppercase tracking-wide opacity-70">
        {label}
      </p>

      <p className="mt-3 text-3xl font-black">{value}</p>
    </div>
  );
}

export default function ApplicationManagement({
  apiBase,
  authHeaders,
  onUnauthorized,
  userRole,
}) {
  const [board, setBoard] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [adminStatus, setAdminStatus] = useState("all");
  const [commissionStatus, setCommissionStatus] = useState("all");

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const [savingStates, setSavingStates] = useState({});
  const [applicationErrors, setApplicationErrors] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  // Confirmation
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [confirmedSalary, setConfirmedSalary] = useState("");
  const [confirmationDate, setConfirmationDate] = useState("");
  const [confirmationNote, setConfirmationNote] = useState("");
  const [confirmationSaving, setConfirmationSaving] = useState(false);
  const [confirmationError, setConfirmationError] = useState("");

  // Messenger
  const [messengerModal, setMessengerModal] = useState(null);
  const [messengerInput, setMessengerInput] = useState("");
  const [messengerSaving, setMessengerSaving] = useState(false);
  const [messengerError, setMessengerError] = useState("");

  const fetchApplicationBoard = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(`${apiBase}/admin/application-board`, {
        headers: authHeaders,
      });

      const incoming = response.data?.data || [];

      const normalized = incoming
        .map((tuition) => ({
          ...tuition,
          applications: [...(tuition.applications || [])].sort(
            (a, b) =>
              new Date(b.appliedTime || 0).getTime() -
              new Date(a.appliedTime || 0).getTime(),
          ),
        }))
        .sort(
          (a, b) =>
            new Date(b.latestApplicationTime || 0).getTime() -
            new Date(a.latestApplicationTime || 0).getTime(),
        );

      setBoard(normalized);
    } catch (err) {
      console.error("Application board fetch error:", err);

      setError(
        err?.response?.data?.message || "Failed to load application board",
      );

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicationBoard();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const allApplications = useMemo(() => {
    return board.flatMap((tuition) => tuition.applications || []);
  }, [board]);

  const summary = useMemo(() => {
    const applications = allApplications;

    return {
      totalApplications: applications.length,

      liveApplications: applications.filter((application) =>
        ["Applied", "Tutor On Demo", "Tutor Confirmed"].includes(
          application.adminStatus,
        ),
      ).length,

      closedApplications: applications.filter(
        (application) => application.adminStatus === "Application Closed",
      ).length,

      rejectedApplications: applications.filter((application) =>
        ["Tutor Rejected Tuition", "Guardian Rejected Tutor"].includes(
          application.adminStatus,
        ),
      ).length,

      lostApplications: applications.filter(
        (application) => application.adminStatus === "Lost",
      ).length,
    };
  }, [allApplications]);

  const filteredBoard = useMemo(() => {
    const query = search.trim().toLowerCase();

    const includesQuery = (value) =>
      String(value || "")
        .toLowerCase()
        .includes(query);

    const result = board
      .map((tuition) => {
        const tuitionMatchesSearch =
          !query ||
          includesQuery(tuition.tuitionId) ||
          includesQuery(`TN${tuition.tuitionId}`) ||
          includesQuery(tuition.studentName) ||
          includesQuery(tuition.posterType) ||
          includesQuery(tuition.tuitionPhone) ||
          includesQuery(tuition.tuitionWhatsapp) ||
          includesQuery(tuition.mediumName) ||
          includesQuery(tuition.levelName) ||
          includesQuery(tuition.subjects) ||
          includesQuery(tuition.areas) ||
          includesQuery(tuition.genderPreference);

        let matchingApplications = [...(tuition.applications || [])];

        if (query && !tuitionMatchesSearch) {
          matchingApplications = matchingApplications.filter(
            (application) =>
              includesQuery(application.applicationId) ||
              includesQuery(application.tutorId) ||
              includesQuery(application.tutorName) ||
              includesQuery(application.tutorEmail) ||
              includesQuery(application.tutorPhone) ||
              includesQuery(application.tutorWhatsapp) ||
              includesQuery(application.instituteName) ||
              includesQuery(application.fieldOfStudyName),
          );
        }

        if (adminStatus !== "all") {
          matchingApplications = matchingApplications.filter(
            (application) => application.adminStatus === adminStatus,
          );
        }

        if (commissionStatus !== "all") {
          matchingApplications = matchingApplications.filter(
            (application) => application.commissionStatus === commissionStatus,
          );
        }

        if (matchingApplications.length === 0) {
          return null;
        }

        matchingApplications.sort(
          (a, b) =>
            new Date(b.appliedTime || 0).getTime() -
            new Date(a.appliedTime || 0).getTime(),
        );

        const newestVisibleApplication =
          matchingApplications[0]?.appliedTime ||
          tuition.latestApplicationTime ||
          null;

        return {
          ...tuition,
          applications: matchingApplications,
          visibleLatestApplicationTime: newestVisibleApplication,
        };
      })
      .filter(Boolean);

    result.sort(
      (a, b) =>
        new Date(b.visibleLatestApplicationTime || 0).getTime() -
        new Date(a.visibleLatestApplicationTime || 0).getTime(),
    );

    return result;
  }, [board, search, adminStatus, commissionStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredBoard.length / pageSize));

  const safePage = Math.min(page, totalPages - 1);

  const paginatedBoard = filteredBoard.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );

  useEffect(() => {
    setPage(0);
  }, [search, adminStatus, commissionStatus, pageSize]);

  const updateApplicationLocally = (applicationId, patch) => {
    setBoard((previous) =>
      previous.map((tuition) => ({
        ...tuition,

        applications: (tuition.applications || []).map((application) =>
          application.applicationId === applicationId
            ? {
                ...application,
                ...patch,
              }
            : application,
        ),
      })),
    );
  };

  const updateApplicationStatus = async (application, nextStatus) => {
    const applicationId = application.applicationId;
    const previousStatus = application.adminStatus;

    if (previousStatus === nextStatus) return;

    setApplicationErrors((previous) => ({
      ...previous,
      [applicationId]: "",
    }));

    setSavingStates((previous) => ({
      ...previous,
      [applicationId]: "saving",
    }));

    updateApplicationLocally(applicationId, {
      adminStatus: nextStatus,
    });

    try {
      await axios.patch(
        `${apiBase}/admin/applications/${applicationId}`,
        {
          admin_status: nextStatus,
        },
        {
          headers: authHeaders,
        },
      );

      setSavingStates((previous) => ({
        ...previous,
        [applicationId]: "saved",
      }));

      window.setTimeout(() => {
        setSavingStates((previous) => ({
          ...previous,
          [applicationId]: "",
        }));
      }, 1200);
    } catch (err) {
      console.error("Application status update error:", err);

      updateApplicationLocally(applicationId, {
        adminStatus: previousStatus,
      });

      setSavingStates((previous) => ({
        ...previous,
        [applicationId]: "error",
      }));

      setApplicationErrors((previous) => ({
        ...previous,
        [applicationId]:
          err?.response?.data?.message || "Failed to save status",
      }));

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    }
  };

  const updateApplicationNote = async (application, note) => {
    const applicationId = application.applicationId;
    const previousNote = application.adminNote || "";

    if (previousNote === note) return;

    setApplicationErrors((previous) => ({
      ...previous,
      [applicationId]: "",
    }));

    setSavingStates((previous) => ({
      ...previous,
      [applicationId]: "saving",
    }));

    updateApplicationLocally(applicationId, {
      adminNote: note,
    });

    try {
      await axios.patch(
        `${apiBase}/admin/applications/${applicationId}`,
        {
          admin_note: note,
        },
        {
          headers: authHeaders,
        },
      );

      setSavingStates((previous) => ({
        ...previous,
        [applicationId]: "saved",
      }));

      window.setTimeout(() => {
        setSavingStates((previous) => ({
          ...previous,
          [applicationId]: "",
        }));
      }, 1200);
    } catch (err) {
      console.error("Application note update error:", err);

      updateApplicationLocally(applicationId, {
        adminNote: previousNote,
      });

      setSavingStates((previous) => ({
        ...previous,
        [applicationId]: "error",
      }));

      setApplicationErrors((previous) => ({
        ...previous,
        [applicationId]:
          err?.response?.data?.message || "Failed to save admin note",
      }));

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    }
  };

  const deleteApplication = async (application) => {
    const applicationId = application.applicationId;

    if (!applicationId) return;

    if (application.commissionRecordId) {
      setApplicationErrors((previous) => ({
        ...previous,
        [applicationId]:
          "This application already has a commission record and cannot be deleted here.",
      }));

      return;
    }

    const confirmed = window.confirm(`Delete Application #${applicationId}?`);

    if (!confirmed) return;

    try {
      setDeletingId(applicationId);

      await axios.delete(`${apiBase}/admin/applications/${applicationId}`, {
        headers: authHeaders,
      });

      setBoard((previous) =>
        previous
          .map((tuition) => ({
            ...tuition,

            applications: (tuition.applications || []).filter(
              (applicationItem) =>
                applicationItem.applicationId !== applicationId,
            ),
          }))
          .filter((tuition) => tuition.applications.length > 0),
      );
    } catch (err) {
      console.error("Application delete error:", err);

      setApplicationErrors((previous) => ({
        ...previous,
        [applicationId]:
          err?.response?.data?.message || "Failed to delete application",
      }));

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openConfirmationModal = (tuition, application) => {
    setConfirmationModal({
      tuition,
      application,
    });

    setConfirmedSalary(tuition.maxSalary || tuition.minSalary || "");
    setConfirmationDate(new Date().toISOString().slice(0, 10));
    setConfirmationNote("");
    setConfirmationError("");
  };

  const closeConfirmationModal = () => {
    if (confirmationSaving) return;

    setConfirmationModal(null);
    setConfirmedSalary("");
    setConfirmationDate("");
    setConfirmationNote("");
    setConfirmationError("");
  };

  const createConfirmation = async () => {
    if (!confirmationModal) return;

    const selectedApplication = confirmationModal.application;
    const tuition = confirmationModal.tuition;

    try {
      setConfirmationSaving(true);
      setConfirmationError("");

      await axios.post(
        `${apiBase}/admin/applications/${selectedApplication.applicationId}/confirmation`,
        {
          confirmed_salary: Number(confirmedSalary),
          confirmation_date: confirmationDate,
          admin_note: confirmationNote.trim() || null,
        },
        {
          headers: authHeaders,
        },
      );

      /*
        Once one tutor is confirmed:
        every other tutor for the same tuition becomes
        "Guardian Rejected Tutor".
      */

      const siblingApplications = (tuition.applications || []).filter(
        (application) =>
          application.applicationId !== selectedApplication.applicationId,
      );

      const siblingResults = await Promise.allSettled(
        siblingApplications.map((application) =>
          axios.patch(
            `${apiBase}/admin/applications/${application.applicationId}`,
            {
              admin_status: "Guardian Rejected Tutor",
            },
            {
              headers: authHeaders,
            },
          ),
        ),
      );

      const failedSiblingUpdates = siblingResults.filter(
        (result) => result.status === "rejected",
      );

      if (failedSiblingUpdates.length > 0) {
        console.error(
          "Some sibling applications could not be rejected:",
          failedSiblingUpdates,
        );
      }

      closeConfirmationModal();

      await fetchApplicationBoard();
    } catch (err) {
      console.error("Confirmation create error:", err);

      setConfirmationError(
        err?.response?.data?.message || "Failed to create confirmation record",
      );

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    } finally {
      setConfirmationSaving(false);
    }
  };

  const openMessengerModal = (application) => {
    setMessengerModal(application);

    setMessengerInput(application.messengerLink || "");

    setMessengerError("");
  };

  const closeMessengerModal = () => {
    if (messengerSaving) return;

    setMessengerModal(null);
    setMessengerInput("");
    setMessengerError("");
  };

  const saveMessengerLink = async () => {
    if (!messengerModal) return;

    const tutorId = messengerModal.tutorId;

    const normalizedLink = normalizeMessengerLink(messengerInput);

    if (!normalizedLink) {
      setMessengerError("Paste the Messenger chat link first.");
      return;
    }

    try {
      new URL(normalizedLink);
    } catch {
      setMessengerError("Please enter a valid Messenger URL.");
      return;
    }

    try {
      setMessengerSaving(true);
      setMessengerError("");

      /*
        Backend requirement:

        PATCH /api/admin/tutors/:id

        body:
        {
          messenger_link: "https://..."
        }

        updateAdminTutor must persist messenger_link
        inside tutor_profiles.
      */

      await axios.patch(
        `${apiBase}/admin/tutors/${tutorId}`,
        {
          messenger_link: normalizedLink,
        },
        {
          headers: authHeaders,
        },
      );

      setBoard((previous) =>
        previous.map((tuition) => ({
          ...tuition,

          applications: (tuition.applications || []).map((application) =>
            application.tutorId === tutorId
              ? {
                  ...application,
                  messengerLink: normalizedLink,
                }
              : application,
          ),
        })),
      );

      closeMessengerModal();
    } catch (err) {
      console.error("Messenger link save error:", err);

      setMessengerError(
        err?.response?.data?.message || "Failed to save Messenger link",
      );

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    } finally {
      setMessengerSaving(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setAdminStatus("all");
    setCommissionStatus("all");
    setPage(0);
  };

  const getTuitionWorkflowStatus = (tuition) => {
    const applications = tuition.applications || [];

    if (
      applications.some(
        (application) => application.adminStatus === "Application Closed",
      )
    ) {
      return {
        label: "Application Closed",
        tone: "success",
      };
    }

    if (
      applications.some(
        (application) => application.adminStatus === "Tutor Confirmed",
      )
    ) {
      return {
        label: "Tutor Confirmed",
        tone: "success",
      };
    }

    if (
      applications.some(
        (application) => application.adminStatus === "Tutor On Demo",
      )
    ) {
      return {
        label: "Tutor On Demo",
        tone: "warning",
      };
    }

    return {
      label: `${applications.length} Tutor${
        applications.length === 1 ? "" : "s"
      } Applied`,
      tone: "blue",
    };
  };

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {/* Page Header */}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-950">
              Application Management
            </h2>

            <p className="mt-1 text-sm leading-6 text-slate-500">
              Manage tuition applications, follow up tutors, confirm tutors, and
              track the complete application workflow.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Pill>Total Applications: {summary.totalApplications}</Pill>

            <Pill tone="blue">Tuitions: {board.length}</Pill>

            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none"
            >
              <option value={5}>5 tuitions per page</option>
              <option value={10}>10 tuitions per page</option>
              <option value={20}>20 tuitions per page</option>
              <option value={50}>50 tuitions per page</option>
            </select>

            <ActionButton onClick={fetchApplicationBoard}>Refresh</ActionButton>
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard
            label="Total Applications"
            value={summary.totalApplications}
          />

          <MetricCard
            label="Live"
            value={summary.liveApplications}
            tone="blue"
          />

          <MetricCard
            label="Closed"
            value={summary.closedApplications}
            tone="success"
          />

          <MetricCard
            label="Rejected"
            value={summary.rejectedApplications}
            tone="warning"
          />

          <MetricCard
            label="Lost"
            value={summary.lostApplications}
            tone="danger"
          />
        </div>

        {/* Search / Filters */}
        <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-4">
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearch();
              }
            }}
            placeholder="Search tuition, tutor, application, phone, area..."
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400 xl:col-span-2"
          />

          <select
            value={adminStatus}
            onChange={(event) => {
              setAdminStatus(event.target.value);
              setPage(0);
            }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-700 outline-none"
          >
            <option value="all">All application statuses</option>

            {ADMIN_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={commissionStatus}
            onChange={(event) => {
              setCommissionStatus(event.target.value);
              setPage(0);
            }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-700 outline-none"
          >
            <option value="all">All commission statuses</option>

            {COMMISSION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <ActionButton variant="dark" onClick={handleSearch}>
            Search
          </ActionButton>

          <ActionButton onClick={clearFilters}>Clear</ActionButton>
        </div>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm font-bold text-slate-500">
              Loading application board...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
              {error}
            </div>
          ) : paginatedBoard.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">
              No tuition applications found.
            </div>
          ) : (
            <div className="space-y-6">
              {paginatedBoard.map((tuition) => {
                const workflowStatus = getTuitionWorkflowStatus(tuition);

                return (
                  <article
                    key={tuition.tuitionId}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                  >
                    {/* Tuition Parent Header */}
                    <div className="border-b border-slate-200 bg-slate-50 p-4">
                      {" "}
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/tuition/${tuition.tuitionId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex"
                            >
                              <Pill tone="blue">
                                <span className="!text-lg !font-bold">
                                  #TN{tuition.tuitionId}
                                </span>
                              </Pill>{" "}
                            </Link>

                            <Pill tone={workflowStatus.tone}>
                              {workflowStatus.label}
                            </Pill>

                            {tuition.tuitionStatus ? (
                              <Pill>{tuition.tuitionStatus}</Pill>
                            ) : null}
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-x-5 gap-y-1.5 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                            {" "}
                            <div>
                              <span className="font-black text-slate-900">
                                Student:
                              </span>{" "}
                              {tuition.studentName || "—"}
                            </div>
                            <div>
                              <span className="font-black text-slate-900">
                                Level:
                              </span>{" "}
                              {tuition.levelName || "—"}
                            </div>
                            <div>
                              <span className="font-black text-slate-900">
                                Medium:
                              </span>{" "}
                              {tuition.mediumName || "—"}
                            </div>
                            <div>
                              <span className="font-black text-slate-900">
                                Gender:
                              </span>{" "}
                              {tuition.genderPreference || "—"}
                            </div>
                            <div className="md:col-span-2">
                              <span className="font-black text-slate-900">
                                Subjects:
                              </span>{" "}
                              {tuition.subjects || "—"}
                            </div>
                            <div>
                              <span className="font-black text-slate-900">
                                Area:
                              </span>{" "}
                              {tuition.areas || "—"}
                            </div>
                            <div>
                              <span className="font-black text-slate-900">
                                Salary:
                              </span>{" "}
                              {tuition.maxSalary
                                ? formatMoney(tuition.maxSalary)
                                : tuition.minSalary
                                  ? formatMoney(tuition.minSalary)
                                  : "—"}
                            </div>
                          </div>

                          <p className="mt-2 text-xs font-semibold text-slate-400">
                            {" "}
                            Latest application:{" "}
                            {formatDate(tuition.latestApplicationTime)}
                          </p>
                        </div>

                        {/* Guardian Contact */}
                        <div className="flex flex-wrap gap-2">
                          {tuition.tuitionPhone ? (
                            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">
                              📞 {tuition.tuitionPhone}
                            </div>
                          ) : null}

                          {tuition.tuitionWhatsapp ? (
                            <a
                              href={whatsappHref(tuition.tuitionWhatsapp)}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition hover:bg-emerald-100"
                            >
                              Guardian WhatsApp
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* Applications / Tutors */}
                    <div className="p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        {" "}
                        <div>
                          <h3 className="text-sm font-black text-slate-900">
                            Applied Tutors
                          </h3>

                          <p className="mt-1 text-xs font-semibold text-slate-400">
                            Newest application first • Scroll horizontally
                          </p>
                        </div>
                        <Pill tone="blue">
                          {tuition.applications.length} Applied
                        </Pill>
                      </div>

                      {/* Horizontal Scroller */}
                      <div className="overflow-x-auto overscroll-x-contain pb-3">
                        <div className="flex w-max snap-x snap-mandatory gap-4">
                          {tuition.applications.map((application) => {
                            const saveState =
                              savingStates[application.applicationId];

                            const applicationError =
                              applicationErrors[application.applicationId];

                            const hasConfirmation = Boolean(
                              application.commissionRecordId,
                            );

                            return (
                              <div
                                key={application.applicationId}
                                className={`w-[300px] min-w-[300px] snap-start rounded-xl border bg-white p-3.5 shadow-sm sm:w-[330px] sm:min-w-[330px] ${
                                  application.adminStatus === "Tutor Confirmed"
                                    ? "border-emerald-300 ring-1 ring-emerald-100"
                                    : application.adminStatus ===
                                        "Guardian Rejected Tutor"
                                      ? "border-red-200"
                                      : "border-slate-200"
                                }`}
                              >
                                {/* Application Header */}
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <Pill tone="blue">
                                      App #{application.applicationId}
                                    </Pill>

                                    <Pill
                                      tone={statusTone(application.adminStatus)}
                                    >
                                      {application.adminStatus}
                                    </Pill>
                                  </div>

                                  {hasConfirmation ? (
                                    <span
                                      className="text-lg"
                                      title="Confirmation created"
                                    >
                                      ✅
                                    </span>
                                  ) : null}
                                </div>

                                {/* Tutor */}
                                <div className="mt-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-base font-black text-slate-950">
                                      {application.tutorName || "Unnamed Tutor"}
                                    </h4>

                                    <Link
                                      to={`/tutor/${application.tutorId}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs font-black text-blue-600 hover:underline"
                                    >
                                      Tutor #{application.tutorId}
                                    </Link>
                                  </div>

                                  <p className="mt-2 text-sm font-semibold text-slate-600">
                                    {application.instituteName || "—"}
                                  </p>

                                  <p className="mt-1 text-sm text-slate-500">
                                    {application.fieldOfStudyName || "—"}
                                  </p>

                                  <p className="mt-2 break-all text-xs font-semibold text-slate-500">
                                    {application.tutorEmail || "—"}
                                  </p>

                                  <p className="mt-2 text-xs font-semibold text-slate-400">
                                    Applied:{" "}
                                    {formatDate(application.appliedTime)}
                                  </p>
                                </div>

                                {/* Tutor Contacts */}
                                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                                  {" "}
                                  {application.tutorPhone ? (
                                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">
                                      📞 {application.tutorPhone}
                                    </div>
                                  ) : null}
                                  {application.tutorWhatsapp ? (
                                    <a
                                      href={whatsappHref(
                                        application.tutorWhatsapp,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition hover:bg-emerald-100"
                                    >
                                      WhatsApp
                                    </a>
                                  ) : null}
                                  {application.messengerLink ? (
                                    <>
                                      <a
                                        href={normalizeMessengerLink(
                                          application.messengerLink,
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-black text-blue-700 transition hover:bg-blue-100"
                                      >
                                        Messenger
                                      </a>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          openMessengerModal(application)
                                        }
                                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50"
                                        title="Edit Messenger link"
                                      >
                                        ✏️
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openMessengerModal(application)
                                      }
                                      className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-black text-blue-700 transition hover:bg-blue-100"
                                    >
                                      ➕ Add Messenger
                                    </button>
                                  )}
                                </div>

                                {/* Status */}
                                <div className="mt-5">
                                  <div className="flex items-center justify-between gap-3">
                                    <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                                      Admin Status
                                    </label>

                                    {saveState === "saving" ? (
                                      <span className="text-xs font-bold text-amber-600">
                                        Saving...
                                      </span>
                                    ) : saveState === "saved" ? (
                                      <span className="text-xs font-bold text-emerald-600">
                                        Saved ✓
                                      </span>
                                    ) : null}
                                  </div>

                                  <select
                                    value={application.adminStatus || "Applied"}
                                    disabled={saveState === "saving"}
                                    onChange={(event) =>
                                      updateApplicationStatus(
                                        application,
                                        event.target.value,
                                      )
                                    }
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-800 outline-none transition focus:border-slate-400 disabled:opacity-60"
                                  >
                                    {ADMIN_STATUSES.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>

                                  {applicationError ? (
                                    <p className="mt-2 text-xs font-bold leading-5 text-red-600">
                                      {applicationError}
                                    </p>
                                  ) : null}
                                </div>

                                {/* Admin Note */}
                                <div className="mt-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                                      Admin Note
                                    </label>

                                    {savingStates[application.applicationId] ===
                                    "saving" ? (
                                      <span className="text-xs font-bold text-amber-600">
                                        Saving...
                                      </span>
                                    ) : savingStates[
                                        application.applicationId
                                      ] === "saved" ? (
                                      <span className="text-xs font-bold text-emerald-600">
                                        Saved ✓
                                      </span>
                                    ) : null}
                                  </div>

                                  <textarea
                                    rows={3}
                                    maxLength={5000}
                                    value={
                                      noteDrafts[application.applicationId] ??
                                      application.adminNote ??
                                      ""
                                    }
                                    onChange={(event) => {
                                      const value = event.target.value;

                                      setNoteDrafts((previous) => ({
                                        ...previous,
                                        [application.applicationId]: value,
                                      }));
                                    }}
                                    onBlur={async () => {
                                      const note =
                                        noteDrafts[application.applicationId] ??
                                        application.adminNote ??
                                        "";

                                      await updateApplicationNote(
                                        application,
                                        note,
                                      );

                                      setNoteDrafts((previous) => {
                                        const next = { ...previous };
                                        delete next[application.applicationId];
                                        return next;
                                      });
                                    }}
                                    placeholder="Internal admin note..."
                                    className="mt-2 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400"
                                  />
                                </div>

                                {/* Commission */}
                                {application.commissionRecordId ? (
                                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Pill tone="success">
                                        Commission #
                                        {application.commissionRecordId}
                                      </Pill>

                                      {application.commissionStatus ? (
                                        <Pill
                                          tone={statusTone(
                                            application.commissionStatus,
                                          )}
                                        >
                                          {application.commissionStatus}
                                        </Pill>
                                      ) : (
                                        <Pill tone="warning">
                                          Payment Pending
                                        </Pill>
                                      )}

                                      <p className="mt-1 text-xs font-semibold text-slate-400">
                                        Payment workflow is handled from
                                        PayTrack.
                                      </p>
                                    </div>

                                    {/* Only main admin can see financial amount */}
                                    {userRole === "admin" &&
                                    application.commissionAmount ? (
                                      <p className="mt-2 text-sm font-black text-slate-800">
                                        Commission:{" "}
                                        {formatMoney(
                                          application.commissionAmount,
                                        )}
                                      </p>
                                    ) : null}

                                    {userRole === "admin" ? (
                                      <p className="mt-1 text-xs font-semibold text-slate-400"></p>
                                    ) : null}
                                  </div>
                                ) : null}

                                {/* Bottom Actions */}
                                <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                                  <button
                                    type="button"
                                    disabled={
                                      deletingId ===
                                        application.applicationId ||
                                      Boolean(application.commissionRecordId)
                                    }
                                    onClick={() =>
                                      deleteApplication(application)
                                    }
                                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-lg transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                                    title={
                                      application.commissionRecordId
                                        ? "Cannot delete after confirmation"
                                        : "Delete application"
                                    }
                                  >
                                    🗑️
                                  </button>

                                  {!application.commissionRecordId ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openConfirmationModal(
                                          tuition,
                                          application,
                                        )
                                      }
                                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-xl transition hover:bg-emerald-100"
                                      title="Confirm this tutor"
                                    >
                                      ✅
                                    </button>
                                  ) : (
                                    <div
                                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-xl"
                                      title="Tutor confirmation created"
                                    >
                                      ✓
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredBoard.length > 0 ? (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <ActionButton
              disabled={safePage <= 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </ActionButton>

            <div className="text-center">
              <p className="text-sm font-semibold text-slate-500">
                Showing tuition{" "}
                <span className="font-black text-slate-900">
                  {safePage * pageSize + 1}
                </span>
                –
                <span className="font-black text-slate-900">
                  {Math.min(
                    safePage * pageSize + pageSize,
                    filteredBoard.length,
                  )}
                </span>{" "}
                of{" "}
                <span className="font-black text-slate-900">
                  {filteredBoard.length}
                </span>
              </p>

              <p className="mt-1 text-xs font-semibold text-slate-400">
                Page {safePage + 1} of {totalPages}
              </p>
            </div>

            <ActionButton
              disabled={safePage >= totalPages - 1}
              onClick={() =>
                setPage((current) => Math.min(totalPages - 1, current + 1))
              }
            >
              Next
            </ActionButton>
          </div>
        ) : null}
      </section>

      {/* Confirmation Modal */}
      {confirmationModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
          onClick={closeConfirmationModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Confirm Tutor
                </h2>

                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Tuition #TN
                  {confirmationModal.tuition.tuitionId} • Application #
                  {confirmationModal.application.applicationId}
                </p>

                <p className="mt-1 text-sm font-black text-slate-800">
                  {confirmationModal.application.tutorName}
                </p>
              </div>

              <button
                type="button"
                disabled={confirmationSaving}
                onClick={closeConfirmationModal}
                className="rounded-xl border border-slate-200 px-3 py-2 font-black text-slate-500 transition hover:bg-slate-50"
              >
                ✕
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {confirmationError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {confirmationError}
                </div>
              ) : null}

              <div>
                <label className="text-sm font-black text-slate-800">
                  Confirmed Salary
                </label>

                <input
                  type="number"
                  min="1"
                  value={confirmedSalary}
                  onChange={(event) => setConfirmedSalary(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:border-slate-400"
                />
              </div>

              <div>
                <label className="text-sm font-black text-slate-800">
                  Confirmation Date
                </label>

                <input
                  type="date"
                  value={confirmationDate}
                  onChange={(event) => setConfirmationDate(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:border-slate-400"
                />
              </div>

              <div>
                <label className="text-sm font-black text-slate-800">
                  Admin Note
                </label>

                <textarea
                  rows={3}
                  value={confirmationNote}
                  onChange={(event) => setConfirmationNote(event.target.value)}
                  placeholder="Optional confirmation note..."
                  className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400 focus:border-slate-400"
                />
              </div>

              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm font-black text-blue-700">
                  Commission: {formatMoney(Number(confirmedSalary || 0) * 0.5)}
                </p>

                <p className="mt-1 text-xs font-semibold leading-5 text-blue-600">
                  A PayTrack commission record will be created. All other tutors
                  under this tuition will become Guardian Rejected Tutor.
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <ActionButton
                disabled={confirmationSaving}
                onClick={closeConfirmationModal}
              >
                Cancel
              </ActionButton>

              <ActionButton
                variant="success"
                disabled={
                  confirmationSaving || !confirmedSalary || !confirmationDate
                }
                onClick={createConfirmation}
              >
                {confirmationSaving ? "Confirming..." : "Confirm Tutor ✓"}
              </ActionButton>
            </div>
          </div>
        </div>
      ) : null}

      {/* Messenger Modal */}
      {messengerModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
          onClick={closeMessengerModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Messenger Chat
                </h2>

                <p className="mt-1 text-sm font-semibold text-slate-500">
                  {messengerModal.tutorName || "Tutor"} • Tutor #
                  {messengerModal.tutorId}
                </p>
              </div>

              <button
                type="button"
                disabled={messengerSaving}
                onClick={closeMessengerModal}
                className="rounded-xl border border-slate-200 px-3 py-2 font-black text-slate-500 transition hover:bg-slate-50"
              >
                ✕
              </button>
            </div>

            <div className="mt-5">
              {messengerError ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {messengerError}
                </div>
              ) : null}

              <label className="text-sm font-black text-slate-800">
                Messenger Chat Link
              </label>

              <input
                type="url"
                value={messengerInput}
                onChange={(event) => setMessengerInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    saveMessengerLink();
                  }
                }}
                placeholder="https://www.messenger.com/t/..."
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400 focus:border-slate-400"
              />

              <p className="mt-2 text-xs font-semibold leading-5 text-slate-400">
                Paste the direct Messenger conversation link for this tutor.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <ActionButton
                disabled={messengerSaving}
                onClick={closeMessengerModal}
              >
                Cancel
              </ActionButton>

              <ActionButton
                variant="blue"
                disabled={messengerSaving || !messengerInput.trim()}
                onClick={saveMessengerLink}
              >
                {messengerSaving ? "Saving..." : "Save Messenger"}
              </ActionButton>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
